import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Link, NavLink, Route, Routes } from 'react-router-dom';
import { useAppShell } from './useAppShell';
import { TabBar } from './components/TabBar';
import { FeedbackLink } from './components/FeedbackLink';
import { startSyncOnReconnect } from './data/sync';

const HomePage = lazy(() => import('./features/home/HomePage'));
const LandingPage = lazy(() => import('./features/landing/LandingPage'));
const JournalPage = lazy(() => import('./features/journal/JournalPage'));
const NewEntryPage = lazy(() => import('./features/journal/NewEntryPage'));
const EntryPage = lazy(() => import('./features/journal/EntryPage'));
const ConditionsPage = lazy(
  () => import('./features/conditions/ConditionsPage'),
);
const SearchPage = lazy(() => import('./features/search/SearchPage'));
const PatternsPage = lazy(() => import('./features/insights/PatternsPage'));
const LurePage = lazy(() => import('./features/lures/LurePage'));
const GearPage = lazy(() => import('./features/gear/GearPage'));
const SpotsPage = lazy(() => import('./features/spots/SpotsPage'));
const NewSpotPage = lazy(() => import('./features/spots/NewSpotPage'));
const SpotPage = lazy(() => import('./features/spots/SpotPage'));
const CommunityPage = lazy(() => import('./features/community/CommunityPage'));
const NewPostPage = lazy(() => import('./features/community/NewPostPage'));
const PostPage = lazy(() => import('./features/community/PostPage'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));
const GuidePage = lazy(() => import('./features/guide/GuidePage'));
const InstallPage = lazy(() => import('./features/install/InstallPage'));
const AdminPage = lazy(() => import('./features/admin/AdminPage'));
const FeedbackPage = lazy(() => import('./features/feedback/FeedbackPage'));
const SignInPage = lazy(() => import('./features/account/SignInPage'));
const RecoverPage = lazy(() => import('./features/account/RecoverPage'));

export default function App() {
  const { updateReady, offlineReady, online, update, dismissOfflineNotice } =
    useAppShell();

  useEffect(() => startSyncOnReconnect(), []);

  return (
    <BrowserRouter>
      <div className="shell">
        {updateReady && (
          <div className="banner" role="status">
            <p>A new version of Happy Fishy is ready.</p>
            <button type="button" onClick={update}>
              Reload now
            </button>
          </div>
        )}

        {!online && (
          <p className="offline" role="status">
            Offline. Everything you save stays on this device until you're back
            in signal.
          </p>
        )}

        <div className="topbar">
          <Link to="/" className="brand">
            <span aria-hidden="true">🐟</span> Happy Fishy
          </Link>
          <nav className="topbar-actions" aria-label="App">
            {/* Text, not an icon: a "?" reads as help with one screen, and
                this is the whole tour of what the app can do. */}
            <NavLink to="/guide" className="topbar-guide">
              Guide
            </NavLink>
            <NavLink to="/gear" className="topbar-guide">
              Gear
            </NavLink>
            <FeedbackLink />
            <Link to="/settings" className="topbar-link" aria-label="Settings">
              ⚙️
            </Link>
          </nav>
        </div>

        <main>
          <Suspense fallback={<p className="muted">Loading…</p>}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/welcome" element={<LandingPage />} />
              <Route path="/journal" element={<JournalPage />} />
              <Route path="/journal/new" element={<NewEntryPage />} />
              <Route path="/journal/:id" element={<EntryPage />} />
              <Route path="/patterns" element={<PatternsPage />} />
              <Route path="/conditions" element={<ConditionsPage />} />
              <Route path="/explore" element={<SearchPage />} />
              <Route path="/lures" element={<LurePage />} />
              <Route path="/lures/:speciesId" element={<LurePage />} />
              <Route path="/gear" element={<GearPage />} />
              <Route path="/spots" element={<SpotsPage />} />
              <Route path="/spots/new" element={<NewSpotPage />} />
              <Route path="/spots/:id" element={<SpotPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/community/new" element={<NewPostPage />} />
              <Route path="/community/:id" element={<PostPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/guide" element={<GuidePage />} />
              <Route path="/install" element={<InstallPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/signin" element={<SignInPage />} />
              {/* Both are links out of an email. */}
              <Route path="/reset" element={<RecoverPage />} />
              <Route path="/verify" element={<RecoverPage />} />
              <Route path="*" element={<HomePage />} />
            </Routes>
          </Suspense>
        </main>

        <TabBar />

        {offlineReady && !updateReady && (
          <div className="toast" role="status">
            <p>Ready to work offline.</p>
            <button type="button" onClick={dismissOfflineNotice}>
              Dismiss
            </button>
          </div>
        )}
      </div>
    </BrowserRouter>
  );
}
